import { supabase } from '../lib/supabase';
import type {
  SalesMaterials,
  AboutCompany,
  PitchBook,
  CompanyMaterial,
  CompanyMaterialCategory,
  OnboardingInput,
  ResearchStatus,
} from '../types/settings';
import { EMPTY_ABOUT_COMPANY, EMPTY_PITCH_BOOK } from '../types/settings';
import { getLocalUser } from '../utils/authHelpers';

export async function getSalesMaterials(): Promise<SalesMaterials | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('sales_materials')
      .select('*')
      .eq('client_id', localUser.client_id)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      if (localUser.role !== 'admin') return null;

      const newRecord: Partial<SalesMaterials> = {
        client_id: localUser.client_id || localUser.id,
        user_id: localUser.email,
        about_company: EMPTY_ABOUT_COMPANY,
        pitch_book: EMPTY_PITCH_BOOK,
        generation_status: 'not_generated',
      };

      const { data: created, error: insertError } = await supabase
        .from('sales_materials')
        .upsert(newRecord, { onConflict: 'client_id' })
        .select()
        .maybeSingle();

      if (insertError) throw insertError;

      return {
        ...created,
        about_company: { ...EMPTY_ABOUT_COMPANY, ...(created.about_company || {}) },
        pitch_book: { ...EMPTY_PITCH_BOOK, ...(created.pitch_book || {}) },
      } as SalesMaterials;
    }

    return {
      ...data,
      about_company: { ...EMPTY_ABOUT_COMPANY, ...(data.about_company || {}) },
      pitch_book: { ...EMPTY_PITCH_BOOK, ...(data.pitch_book || {}) },
    } as SalesMaterials;
  } catch (error) {
    console.error('Error fetching sales materials:', error);
    return null;
  }
}

export async function saveAboutCompany(aboutCompany: AboutCompany): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('sales_materials')
      .update({ about_company: aboutCompany })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving about company:', error);
    return false;
  }
}

export async function savePitchBook(pitchBook: PitchBook): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('sales_materials')
      .update({ pitch_book: pitchBook })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error saving pitch book:', error);
    return false;
  }
}

export async function updateGenerationStatus(
  status: 'not_generated' | 'generating' | 'generated'
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('sales_materials')
      .update({ generation_status: status })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating generation status:', error);
    return false;
  }
}

export async function generatePitchBookContent(
  section: 'hooks' | 'elaborations' | 'engaging_questions' | 'qualifying_questions' | 'objection_response',
  aboutCompany: AboutCompany,
  existingItem?: { title?: string; category?: string },
  count: number = 3,
  existingItems?: string[]
): Promise<string[] | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const filteredExisting = existingItems?.filter(t => t.trim().length > 0);

    const response = await fetch(`${supabaseUrl}/functions/v1/generate-sales-materials`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        section,
        about_company: aboutCompany,
        existing_item: existingItem,
        existing_items: filteredExisting?.length ? filteredExisting : undefined,
        count,
      }),
    });

    if (!response.ok) throw new Error('Failed to generate content');

    const data = await response.json();
    return data.generated_items;
  } catch (error) {
    console.error('Error generating pitch book content:', error);
    return null;
  }
}

export async function getCompanyMaterials(): Promise<CompanyMaterial[]> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { data } = await supabase
      .from('sales_materials')
      .select('company_materials')
      .eq('client_id', localUser.client_id)
      .maybeSingle();

    if (!data?.company_materials) return [];

    return data.company_materials as CompanyMaterial[];
  } catch (error) {
    console.error('Error fetching company materials:', error);
    return [];
  }
}

export async function uploadCompanyMaterial(file: File): Promise<CompanyMaterial | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.id || !localUser?.client_id) throw new Error('Not authenticated');

    const timestamp = Date.now();
    const fileName = `${localUser.id}/${timestamp}_${file.name}`;

    const { error: uploadError } = await supabase.storage
      .from('company-materials')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('company-materials')
      .getPublicUrl(fileName);

    const material: CompanyMaterial = {
      id: timestamp.toString(),
      name: file.name,
      url: publicUrl,
      size: file.size,
      type: file.type,
      uploaded_at: new Date().toISOString(),
      source: 'upload',
    };

    const currentMaterials = await getCompanyMaterials();
    const updatedMaterials = [...currentMaterials, material];

    const { error: updateError } = await supabase
      .from('sales_materials')
      .update({ company_materials: updatedMaterials })
      .eq('client_id', localUser.client_id);

    if (updateError) throw updateError;

    return material;
  } catch (error) {
    console.error('Error uploading company material:', error);
    return null;
  }
}

export async function addCompanyMaterialLink(
  name: string,
  url: string,
  category?: CompanyMaterialCategory,
  description?: string
): Promise<CompanyMaterial | null> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const material: CompanyMaterial = {
      id: Date.now().toString(),
      name,
      url,
      size: 0,
      type: 'link',
      uploaded_at: new Date().toISOString(),
      source: 'link',
      category,
      description,
    };

    const currentMaterials = await getCompanyMaterials();
    const updatedMaterials = [...currentMaterials, material];

    const { error } = await supabase
      .from('sales_materials')
      .update({ company_materials: updatedMaterials })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return material;
  } catch (error) {
    console.error('Error adding company material link:', error);
    return null;
  }
}

export async function updateCompanyMaterial(
  materialId: string,
  updates: { category?: CompanyMaterialCategory; description?: string }
): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const currentMaterials = await getCompanyMaterials();
    const updatedMaterials = currentMaterials.map(m =>
      m.id === materialId ? { ...m, ...updates } : m
    );

    const { error } = await supabase
      .from('sales_materials')
      .update({ company_materials: updatedMaterials })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating company material:', error);
    return false;
  }
}

export async function markAsCompanyProfile(materialId: string): Promise<{ success: boolean; profileUrl?: string }> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const currentMaterials = await getCompanyMaterials();
    const material = currentMaterials.find(m => m.id === materialId);
    if (!material) return { success: false };

    const updatedMaterials = currentMaterials.map(m => ({
      ...m,
      is_company_profile: m.id === materialId,
    }));

    const { error: matError } = await supabase
      .from('sales_materials')
      .update({ company_materials: updatedMaterials })
      .eq('client_id', localUser.client_id);
    if (matError) throw matError;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const response = await fetch(`${supabaseUrl}/functions/v1/upload-profile-to-drive`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: localUser.client_id,
        file_url: material.url,
        file_name: material.name,
        file_type: material.type,
      }),
    });

    if (!response.ok) {
      const errBody = await response.text();
      throw new Error(`Drive upload failed: ${errBody}`);
    }

    const { profile_url } = await response.json();

    const { error: settingsError } = await supabase
      .from('user_settings')
      .update({
        company_profile_url: profile_url,
        company_profile_material_id: materialId,
      })
      .eq('client_id', localUser.client_id);
    if (settingsError) throw settingsError;

    return { success: true, profileUrl: profile_url };
  } catch (error) {
    console.error('Error marking company profile:', error);
    return { success: false };
  }
}

export async function unmarkCompanyProfile(): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const currentMaterials = await getCompanyMaterials();
    const updatedMaterials = currentMaterials.map(m => ({
      ...m,
      is_company_profile: false,
    }));

    const { error: matError } = await supabase
      .from('sales_materials')
      .update({ company_materials: updatedMaterials })
      .eq('client_id', localUser.client_id);
    if (matError) throw matError;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const deleteResp = await fetch(`${supabaseUrl}/functions/v1/upload-profile-to-drive`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ client_id: localUser.client_id }),
    });
    if (!deleteResp.ok) {
      const errBody = await deleteResp.text().catch(() => '');
      throw new Error(`Drive delete failed: ${errBody}`);
    }

    const { error: settingsError } = await supabase
      .from('user_settings')
      .update({
        company_profile_url: null,
        company_profile_material_id: null,
      })
      .eq('client_id', localUser.client_id);
    if (settingsError) throw settingsError;

    return true;
  } catch (error) {
    console.error('Error unmarking company profile:', error);
    return false;
  }
}

export async function deleteCompanyMaterial(materialId: string): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const currentMaterials = await getCompanyMaterials();
    const material = currentMaterials.find(m => m.id === materialId);

    if (!material) return false;

    if (material.is_company_profile) {
      await unmarkCompanyProfile();
    }

    if (material.source !== 'link' && material.type !== 'link') {
      const filePath = material.url.split('/').slice(-2).join('/');
      await supabase.storage
        .from('company-materials')
        .remove([filePath]);
    }

    const updatedMaterials = currentMaterials.filter(m => m.id !== materialId);

    const { error } = await supabase
      .from('sales_materials')
      .update({ company_materials: updatedMaterials })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting company material:', error);
    return false;
  }
}

export async function startCompanyResearch(
  input: OnboardingInput
): Promise<{ success: boolean; error?: string }> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.email) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/research-company`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        ...input,
        user_id: localUser.email,
        client_id: localUser.client_id || localUser.id,
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || 'Research request failed' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error starting company research:', error);
    return { success: false, error: 'Failed to start research' };
  }
}

export async function getResearchStatus(): Promise<{
  status: ResearchStatus;
  error: string | null;
}> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { data } = await supabase
      .from('sales_materials')
      .select('research_status, research_error')
      .eq('client_id', localUser.client_id)
      .maybeSingle();

    return {
      status: (data?.research_status as ResearchStatus) || 'idle',
      error: data?.research_error || null,
    };
  } catch (error) {
    console.error('Error getting research status:', error);
    return { status: 'idle', error: null };
  }
}

export async function resetResearchStatus(): Promise<boolean> {
  try {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('sales_materials')
      .update({ research_status: 'idle', research_error: null })
      .eq('client_id', localUser.client_id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error resetting research status:', error);
    return false;
  }
}

export async function generateAllAgentScripts(
  salesMaterials: SalesMaterials
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    await updateGenerationStatus('generating');

    const response = await fetch(`${supabaseUrl}/functions/v1/generate-all-agent-scripts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        about_company: salesMaterials.about_company,
        pitch_book: salesMaterials.pitch_book,
        user_id: salesMaterials.user_id,
        client_id: salesMaterials.client_id,
      }),
    });

    if (!response.ok) {
      await updateGenerationStatus('not_generated');
      throw new Error('Failed to generate agent scripts');
    }

    const data = await response.json();

    if (data.success) {
      await updateGenerationStatus('generated');
      return { success: true };
    } else {
      await updateGenerationStatus('not_generated');
      return { success: false, error: data.error || 'Generation failed' };
    }
  } catch (error) {
    console.error('Error generating all agent scripts:', error);
    await updateGenerationStatus('not_generated');
    return { success: false, error: 'Failed to generate agent scripts' };
  }
}
